import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Button } from "./ui/button";
import {
  activities,
  activityMetaDate,
  todayISO,
  CATEGORY_LABELS,
} from "../data/activities-data";
import ReserveModal from "./ReserveModal";
import transition from "./transition";
import "../style/Activities.css";

const TABS = [
  { id: "latest", label: "Latest" },
  { id: "classes", label: "Classes" },
  { id: "events", label: "Events" },
  { id: "media", label: "Media" },
  { id: "merch", label: "Merch" },
];

// Tabs where the activity's event date (not publish date) drives ordering.
const DATE_GROUPED_TABS = ["classes", "events"];

const listVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2, ease: "easeIn" } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

const eventKey = (activity) => activity.eventDate || activity.publishedAt;

const storyPath = (activity) => `/activities/${activity.id}`;

const ActivityMedia = ({ activity }) => {
  const [playing, setPlaying] = useState(false);

  if (activity.videoId) {
    return playing ? (
      <iframe
        className="activity-media activity-media-embed"
        src={`https://www.youtube-nocookie.com/embed/${activity.videoId}?rel=0&showinfo=0&autoplay=1`}
        title={activity.title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      ></iframe>
    ) : (
      <button
        type="button"
        className="activity-media activity-media-thumb"
        onClick={() => setPlaying(true)}
        aria-label={`Play video: ${activity.title}`}
      >
        <img
          className="activity-media-img grayscale"
          src={`https://img.youtube.com/vi/${activity.videoId}/hqdefault.jpg`}
          alt={activity.title}
          loading="lazy"
        />
        <span className="activity-media-overlay">▶&nbsp; Watch</span>
      </button>
    );
  }

  if (activity.image) {
    const img = (
      <img
        className="activity-media-img grayscale"
        src={activity.image}
        alt={activity.title}
        loading="lazy"
      />
    );

    // A story's featured image leads into the story itself.
    return activity.body ? (
      <Link to={storyPath(activity)} className="activity-media activity-media-thumb">
        {img}
        <span className="activity-media-overlay">Read the Story</span>
      </Link>
    ) : (
      <div className="activity-media">{img}</div>
    );
  }

  return null;
};

const ActivityRow = ({ activity, onReserve }) => {
  const detail = [activity.location, activity.time].filter(Boolean).join("  ·  ");
  const hasStory = Boolean(activity.body);
  // The right-hand button is for the money action (tickets / shop / reserve).
  // "Read the Story" is only promoted to a button when it's the sole action.
  const hasPrimaryCta = Boolean(activity.reserve || activity.link);

  return (
    <motion.article className="activity-row" variants={itemVariants}>
      <div className="activity-meta">
        <p className="activity-date">{activityMetaDate(activity)}</p>
        <div className="activity-tags">
          {activity.categories.map((c) => (
            <span key={c} className="activity-tag">
              {CATEGORY_LABELS[c]}
            </span>
          ))}
        </div>
      </div>

      <div className="activity-body">
        <h3 className="activity-title">
          {hasStory ? (
            <Link to={storyPath(activity)} className="activity-title-link">
              {activity.title}
            </Link>
          ) : (
            activity.title
          )}
        </h3>
        {activity.description && (
          <p className="activity-description">{activity.description}</p>
        )}
        {detail && <p className="activity-detail">{detail}</p>}
        <ActivityMedia activity={activity} />
        {hasStory && hasPrimaryCta && (
          <Link to={storyPath(activity)} className="activity-read-more">
            Read the Story <ArrowUpRight className="activity-icon" />
          </Link>
        )}
      </div>

      <div className="activity-action">
        {activity.reserve ? (
          <Button
            variant="editorial"
            disabled={activity.isFull}
            onClick={() => onReserve(activity)}
          >
            {activity.isFull ? "Class is Full" : "Reserve Spot"}
            {!activity.isFull && <ArrowUpRight className="activity-icon" />}
          </Button>
        ) : activity.link ? (
          <a
            href={activity.link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="activity-link"
          >
            <Button variant="outline">
              {activity.link.label}
              <ArrowUpRight className="activity-icon" />
            </Button>
          </a>
        ) : hasStory ? (
          <Link to={storyPath(activity)} className="activity-link">
            <Button variant="editorial">
              Read the Story
              <ArrowUpRight className="activity-icon" />
            </Button>
          </Link>
        ) : null}
      </div>
    </motion.article>
  );
};

const Activities = () => {
  const [activeTab, setActiveTab] = useState("latest");
  const [reserveFor, setReserveFor] = useState(null);

  const sections = useMemo(() => {
    if (activeTab === "latest") {
      const items = [...activities].sort((a, b) =>
        b.publishedAt.localeCompare(a.publishedAt)
      );
      return [{ label: null, items }];
    }

    const items = activities.filter((a) => a.categories.includes(activeTab));

    if (DATE_GROUPED_TABS.includes(activeTab)) {
      const today = todayISO();
      const upcoming = items
        .filter((a) => eventKey(a) >= today)
        .sort((a, b) => eventKey(a).localeCompare(eventKey(b)));
      const past = items
        .filter((a) => eventKey(a) < today)
        .sort((a, b) => eventKey(b).localeCompare(eventKey(a)));

      const result = [];
      if (upcoming.length) result.push({ label: "Upcoming", items: upcoming });
      if (past.length) result.push({ label: "Past", items: past });
      return result;
    }

    items.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
    return [{ label: null, items }];
  }, [activeTab]);

  const isEmpty = sections.every((s) => s.items.length === 0) || sections.length === 0;

  return (
    <div className="activities-page">
      {reserveFor && (
        <ReserveModal
          title={`${reserveFor.title} — ${activityMetaDate(reserveFor)}`}
          onClose={() => setReserveFor(null)}
        />
      )}

      <div className="activities-inner">
        <h1 className="activities-main-title">ACTIVITIES</h1>

        <div className="activities-tabs" role="tablist" aria-label="Activity categories">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`activities-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {activeTab === tab.id && (
                <motion.span
                  className="activities-tab-underline"
                  layoutId="activities-tab-underline"
                  transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            className={`activities-list ${isEmpty ? "is-empty" : ""}`}
            variants={listVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {isEmpty && (
              <p className="activities-empty">
                Nothing here yet — check back soon.
              </p>
            )}

            {sections.map((section) => (
              <section key={section.label || "all"} className="activities-section">
                {section.label && (
                  <p className="activities-section-label">{section.label}</p>
                )}
                {section.items.map((activity) => (
                  <ActivityRow
                    key={activity.id}
                    activity={activity}
                    onReserve={setReserveFor}
                  />
                ))}
              </section>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default transition(Activities);
